import { FeaturedArticle, useStyles } from '@archipro-design/aria';
import { Link, useNavigate } from '@remix-run/react';
import * as S from './FeaturedCarousel.style';
import React, { useRef } from 'react';
import type { FeaturedCarouselProps } from './FeaturedCarousel';
import type { FeaturedSlideProps } from '../../type';
import type { AdvertCreative } from '@modules/adverts/type';
import { ClientOnly } from 'remix-utils';
import {
    useAdEventHandler,
    useLogImpression,
    useTracker,
} from '@archipro-website/tracker';
import { useLocalisation } from '@archipro-website/localisation';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';
import { createLinkedComponentParams } from '~/utils/urlHelper';
import { useTileHotButtons } from '~/modules/root';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
    Navigation,
    Pagination,
    Keyboard,
    Autoplay,
    EffectFade,
} from 'swiper/modules';
import classnames from 'classnames';
import { ArrowLeftSlim, ArrowRightSlim } from '@archipro-design/icons';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

const FeaturedCarouselDesktop: React.FC<FeaturedCarouselProps> = ({
    featuredSlides,
    imageLazyLoading,
    backgroundColor,
}) => {
    const { css } = useStyles();
    const navigate = useNavigate();
    const { getTileTopActions } = useTileHotButtons({ itemType: 'article' });

    const carouselItems = React.useMemo(() => {
        return featuredSlides.map((article, index) => {
            const topActions = getTileTopActions({ item: article });
            const showLazyLoad =
                imageLazyLoading === 'lazy' ? true : index !== 0;

            if (article.ad) {
                return (
                    <CarouselAdItem
                        key={index}
                        article={article}
                        backgroundColor={backgroundColor}
                        showLazyLoad={showLazyLoad}
                        ad={article.ad}
                        topActions={topActions}
                    />
                );
            } else {
                if (
                    article.hasVideo &&
                    article.video &&
                    article.video.playsInline
                ) {
                    return (
                        <ClientOnly
                            fallback={<></>}
                            key={`featuredArticle-client-only-${article.ID}`}
                        >
                            {() => (
                                <CarouselItem
                                    key={index}
                                    article={{
                                        ...article,
                                        video: {
                                            ...article.video,
                                            src:
                                                (article.video &&
                                                    article.video.src) ||
                                                '',
                                            onClickPreview: () => {
                                                navigate(
                                                    `${article.link}?video=1`
                                                );
                                            },
                                            trackPlayEvent: () => {}, // Video never actually plays here
                                        },
                                    }}
                                    backgroundColor={backgroundColor}
                                    showLazyLoad={showLazyLoad}
                                    topActions={topActions}
                                />
                            )}
                        </ClientOnly>
                    );
                }
                return (
                    <CarouselItem
                        key={index}
                        article={article}
                        backgroundColor={backgroundColor}
                        showLazyLoad={showLazyLoad}
                        topActions={topActions}
                    />
                );
            }
        });
    }, [
        backgroundColor,
        featuredSlides,
        imageLazyLoading,
        navigate,
        getTileTopActions,
    ]);

    return (
        <div className={css(S.Wrap)}>
            <Swiper
                navigation={{
                    nextEl: '.slider-button-next',
                    prevEl: '.slider-button-prev',
                }}
                allowSlideNext={true}
                allowSlidePrev={true}
                loop={true}
                autoplay={{
                    delay: 5000,
                    pauseOnMouseEnter: true,
                    disableOnInteraction: false,
                }}
                effect={'fade'}
                speed={300}
                allowTouchMove={false}
                fadeEffect={{
                    crossFade: true,
                }}
                modules={[
                    Navigation,
                    Pagination,
                    Keyboard,
                    Autoplay,
                    EffectFade,
                ]}
            >
                {carouselItems.map((item, index) => (
                    <SwiperSlide key={`slide-${index}`}>{item}</SwiperSlide>
                ))}
                <div className={css(S.SliderNavigation)}>
                    <button
                        className={classnames(
                            css(S.SliderNavButton),
                            'slider-button-prev'
                        )}
                    >
                        <ArrowLeftSlim />
                    </button>
                    <button
                        className={classnames(
                            css(S.SliderNavButton),
                            'slider-button-next'
                        )}
                    >
                        <ArrowRightSlim />
                    </button>
                </div>
            </Swiper>
        </div>
    );
};

const CarouselItem: React.FC<{
    article: FeaturedSlideProps;
    showLazyLoad: boolean;
    backgroundColor?: string;
    topActions?: React.ReactNode;
}> = ({ article, showLazyLoad, backgroundColor, topActions }) => {
    const {
        ID,
        image,
        link,
        title,
        categoryLabel,
        professionalID: _professionalID,
        canEdit: _canEdit,
        bmEditorLink: _bmEditorLink,
        date,
        ...rest
    } = article;
    const { css } = useStyles();
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    const { localiseDate } = useLocalisation();
    const ref = useRef(null);
    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: ID,
            professionalID: article.professionalID,
        },
        ref,
    });

    return (
        <div key={`featuredArticle-${ID}`}>
            <FeaturedArticle
                {...rest}
                ref={ref}
                image={{
                    ...image,
                    loading: showLazyLoad ? 'lazy' : 'eager',
                    overlay: {
                        as: Link,
                        to: link,
                        icon: <></>,
                    },
                    alt: title,
                }}
                className={css(S.Tile)}
                onFavourite={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFollow({ id: ID }, 'ArticlePin');
                }}
                date={getFormattedLocalisedDate(localiseDate, date)}
                isFavourited={isInDesignBoard(ID, 'ArticlePin')}
                variables={{
                    backgroundColor: backgroundColor,
                    overlayBackgroundColor: `rgba(0, 0, 0, 0)`,
                }}
                categoryTitle={categoryLabel}
                title={{
                    children: title,
                    as: Link,
                    to: link,
                    link: true,
                }}
                topActions={topActions}
            />
        </div>
    );
};

const CarouselAdItem: React.FC<{
    article: FeaturedSlideProps;
    showLazyLoad: boolean;
    backgroundColor?: string;
    ad: AdvertCreative;
    topActions?: React.ReactNode;
}> = ({ article, showLazyLoad, backgroundColor, ad, topActions }) => {
    const {
        ID,
        image,
        link,
        title,
        label,
        professionalID: _professionalID,
        canEdit: _canEdit,
        bmEditorLink: _bmEditorLink,
        date,
        ...rest
    } = article;

    const ref = useRef(null);
    const { onClick } = useAdEventHandler(ad, ref);
    const { css } = useStyles();
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    const { localiseDate } = useLocalisation();
    const tracker = useTracker();

    return (
        <div key={`featuredArticle-${ID}`}>
            <FeaturedArticle
                ref={ref}
                {...rest}
                date={getFormattedLocalisedDate(localiseDate, date)}
                image={{
                    ...image,
                    loading: showLazyLoad ? 'lazy' : 'eager',
                    overlay: {
                        ...createLinkedComponentParams(
                            tracker,
                            'Ad_ImageClick',
                            link,
                            {
                                libraryItemID: ad.professional.id,
                                professionalID: ad.professional.id,
                                professionalTitle: ad.professional.title,
                            },
                            null,
                            onClick
                        ),
                        icon: <></>,
                    },
                    alt: title,
                }}
                className={css(S.Tile)}
                onFavourite={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFollow({ id: ID }, 'ArticlePin');
                }}
                isFavourited={isInDesignBoard(ID, 'ArticlePin')}
                variables={{
                    backgroundColor: backgroundColor,
                    overlayBackgroundColor: `rgba(0, 0, 0, 0)`,
                }}
                categoryTitle={label}
                title={{
                    children: title,
                    ...createLinkedComponentParams(
                        tracker,
                        'Ad_CTAClick',
                        link,
                        {
                            libraryItemID: ad.professional.id,
                            professionalID: ad.professional.id,
                            professionalTitle: ad.professional.title,
                        },
                        null,
                        onClick
                    ),
                    link: true,
                }}
                topActions={topActions}
            />
        </div>
    );
};

export default FeaturedCarouselDesktop;
